import React, { useState, useMemo } from 'react';
import { User, Copy, Check, Share2, Users, RefreshCw, Trophy, Crown, Star, ChevronUp } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useNostrQuery, useNostrProfile } from '../hooks/useNostrQuery';
import { useAppContext } from '../hooks/useAppContext';
import FollowButton from '../components/FollowButton';
import UserBadges from '../components/UserBadges';
import { getRecruiterBadge, RECRUITER_TIERS } from '../lib/badges';
import { REFERRAL_KIND, REFERRAL_DRTAG } from '../lib/referral';

type Range = 'monthly' | 'alltime';

const MONTHLY_SINCE = Math.floor(Date.now() / 1000) - 60 * 60 * 24 * 30;

function buildScores(events: import('nostr-tools').Event[]) {
  const scores: Record<string, Set<string>> = {};
  events.forEach(ev => {
    const ref = ev.tags.find(t => t[0] === 'ref')?.[1];
    if (ref && ev.pubkey !== ref) {
      if (!scores[ref]) scores[ref] = new Set();
      scores[ref].add(ev.pubkey);
    }
  });
  return Object.entries(scores)
    .map(([pk, s]) => ({ pubkey: pk, count: s.size }))
    .sort((a, b) => b.count - a.count);
}

export default function LeaderboardPage() {
  const { user } = useAppContext();
  const [range, setRange] = useState<Range>('monthly');
  const [copied, setCopied] = useState(false);

  // All-time referral events — query all relays for full coverage
  const { events: eventsAllTime, loading: loadingAllTime, refetch } = useNostrQuery({
    filters: [{ kinds: [REFERRAL_KIND], '#d': [REFERRAL_DRTAG], limit: 1000 }],
    enabled: true,
    allRelays: true,
  });

  // Monthly referral events — query all relays for full coverage
  const { events: eventsMonthly, loading: loadingMonthly } = useNostrQuery({
    filters: [{ kinds: [REFERRAL_KIND], '#d': [REFERRAL_DRTAG], limit: 1000, since: MONTHLY_SINCE }],
    enabled: true,
    allRelays: true,
  });

  const scoresAllTime = useMemo(() => buildScores(eventsAllTime), [eventsAllTime]);
  const scoresMonthly = useMemo(() => buildScores(eventsMonthly), [eventsMonthly]);
  const scores = range === 'monthly' ? scoresMonthly : scoresAllTime;

  const loading = range === 'monthly' ? loadingMonthly : loadingAllTime;

  // My stats (always based on all-time for badge display)
  const myInviteLink = user ? `https://bchnostr.com?ref=${user.pubkey}` : '';
  const myAllTimeCount = user ? (scoresAllTime.find(r => r.pubkey === user.pubkey)?.count ?? 0) : 0;
  const myMonthlyCount = user ? (scoresMonthly.find(r => r.pubkey === user.pubkey)?.count ?? 0) : 0;
  const myRank = user ? scores.findIndex(r => r.pubkey === user.pubkey) : -1;
  const myBadge = myAllTimeCount > 0 ? getRecruiterBadge(myAllTimeCount) : null;
  const nextTier = [...RECRUITER_TIERS].reverse().find(t => myAllTimeCount < t.minReferrals);

  const nudge = useMemo(() => {
    if (!user) return null;
    if (myRank < 0) return range === 'monthly' ? 'Share your link to get on the board this month! 🚀' : 'Share your link to claim your spot! 🚀';
    if (myRank === 0) return "You're #1! 👑 Keep spreading the word!";
    const diff = scores[myRank - 1].count - (range === 'monthly' ? myMonthlyCount : myAllTimeCount);
    return `You're #${myRank + 1} — invite ${diff} more friend${diff !== 1 ? 's' : ''} to reach #${myRank}! 🔥`;
  }, [user, myRank, range, scores, myMonthlyCount, myAllTimeCount]);

  const top3 = scores.slice(0, 3);
  const rest = scores.slice(3);

  return (
    <div className="pb-8">

      {/* ── Hero header ── */}
      <div className="relative overflow-hidden bg-gradient-to-b from-amber-500/10 via-primary/5 to-transparent px-4 pt-6 pb-4">
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center gap-2">
            <Trophy className="w-6 h-6 text-amber-500" />
            <h1 className="text-xl font-extrabold text-foreground">Ambassador Hall of Fame</h1>
          </div>
          <button
            onClick={() => refetch()}
            className="p-1.5 rounded-lg text-muted-foreground hover:text-primary hover:bg-muted transition-colors"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
        <p className="text-sm text-muted-foreground mb-4">
          Every person here helped grow BCH Nostr. 🌱
        </p>

        {/* Monthly / All-Time toggle */}
        <div className="flex gap-2">
          {([['monthly', '\uD83D\uDDD3 This Month'], ['alltime', '🏆 All-Time']] as const).map(([r, label]) => (
            <button
              key={r}
              onClick={() => setRange(r)}
              className={`px-3 py-1.5 rounded-full text-sm font-semibold transition-colors ${
                range === r
                  ? 'bg-amber-500/20 text-amber-600 dark:text-amber-400 border border-amber-500/30'
                  : 'bg-muted text-muted-foreground hover:text-foreground'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* ── My stats card (logged in) ── */}
      {user && (
        <div className="mx-4 mb-4">
          <div className="rounded-2xl border border-primary/20 bg-gradient-to-br from-primary/8 to-transparent p-4 space-y-3">
            {/* Top row: badge + nudge */}
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-2xl bg-amber-500/10 flex items-center justify-center shrink-0">
                {myBadge ? (
                  <span className="text-xl">{myBadge.emoji}</span>
                ) : (
                  <Star className="w-5 h-5 text-amber-500/60" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                {myBadge && (
                  <span className={`inline-flex items-center gap-0.5 px-2 py-0.5 rounded-full text-xs font-bold border mb-0.5 ${myBadge.bgColor} ${myBadge.color}`}>
                    {myBadge.emoji} {myBadge.name}
                  </span>
                )}
                <p className="text-sm font-semibold text-foreground leading-snug">{nudge}</p>
              </div>
            </div>

            {/* Stats row */}
            <div className="grid grid-cols-3 gap-2">
              <div className="text-center py-2 rounded-xl bg-muted/50">
                <p className="text-lg font-extrabold tabular-nums">{myAllTimeCount}</p>
                <p className="text-[11px] text-muted-foreground">All-time</p>
              </div>
              <div className="text-center py-2 rounded-xl bg-muted/50">
                <p className="text-lg font-extrabold tabular-nums">{myMonthlyCount}</p>
                <p className="text-[11px] text-muted-foreground">This month</p>
              </div>
              {nextTier ? (
                <div className="text-center py-2 rounded-xl bg-muted/50">
                  <p className="text-lg font-extrabold tabular-nums">{nextTier.minReferrals - myAllTimeCount}</p>
                  <p className="text-[11px] text-muted-foreground">to {nextTier.emoji} {nextTier.name.replace('BCH Nostr ', '').replace('BCH ', '')}</p>
                </div>
              ) : (
                <div className="text-center py-2 rounded-xl bg-amber-500/10 border border-amber-500/20">
                  <p className="text-lg">👑</p>
                  <p className="text-[11px] text-amber-500 font-bold">Max rank!</p>
                </div>
              )}
            </div>

            {/* Invite link */}
            <div className="flex items-center gap-2">
              <div className="flex-1 bg-muted rounded-xl px-3 py-2 min-w-0">
                <code className="text-xs font-mono truncate block text-muted-foreground">{myInviteLink}</code>
              </div>
              <button
                onClick={() => { navigator.clipboard.writeText(myInviteLink).catch(() => {}); setCopied(true); setTimeout(() => setCopied(false), 2000); }}
                className="p-2.5 rounded-xl border border-border hover:bg-muted transition-colors shrink-0"
              >
                {copied ? <Check className="w-4 h-4 text-emerald-500" /> : <Copy className="w-4 h-4 text-muted-foreground" />}
              </button>
              <button
                onClick={() => navigator.share?.({ title: 'Join BCHNostr', text: 'Join the Bitcoin Cash social network!', url: myInviteLink }).catch(() => {})}
                className="p-2.5 rounded-xl bg-primary text-primary-foreground hover:bg-primary/90 transition-colors shrink-0"
              >
                <Share2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Loading ── */}
      {loading && scores.length === 0 && (
        <div className="flex items-center justify-center py-16">
          <div className="w-8 h-8 border-2 border-primary/20 border-t-primary rounded-full animate-spin" />
        </div>
      )}

      {/* ── Empty state ── */}
      {!loading && scores.length === 0 && (
        <div className="text-center py-16 px-6">
          <div className="text-5xl mb-3">🥇🥈🥉</div>
          <p className="text-base font-bold text-foreground mb-1">
            {range === 'monthly' ? 'No ambassadors this month yet' : 'No ambassadors yet'}
          </p>
          <p className="text-sm text-muted-foreground">Be the first to claim the top spot!</p>
        </div>
      )}

      {/* ── Top 3 podium ── */}
      {top3.length > 0 && (
        <div className="px-4 mb-2">
          <div className="flex items-end justify-center gap-3">
            {/* 2nd */}
            {top3[1] ? (
              <PodiumCard entry={top3[1]} rank={2} isMe={user?.pubkey === top3[1].pubkey} />
            ) : <div className="flex-1" />}
            {/* 1st */}
            <PodiumCard entry={top3[0]} rank={1} isMe={user?.pubkey === top3[0].pubkey} />
            {/* 3rd */}
            {top3[2] ? (
              <PodiumCard entry={top3[2]} rank={3} isMe={user?.pubkey === top3[2].pubkey} />
            ) : <div className="flex-1" />}
          </div>
        </div>
      )}

      {/* ── Rest of the list ── */}
      {rest.length > 0 && (
        <div className="mt-2 divide-y divide-border">
          {rest.map((entry, i) => (
            <AmbassadorRow
              key={entry.pubkey}
              pubkey={entry.pubkey}
              count={entry.count}
              rank={i + 4}
              isMe={user?.pubkey === entry.pubkey}
            />
          ))}
        </div>
      )}

      {/* ── Rank guide ── */}
      <div className="mx-4 mt-6 rounded-2xl border border-border bg-muted/30 p-4 space-y-3">
        <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider">Ambassador Ranks</p>
        <div className="space-y-2.5">
          {[...RECRUITER_TIERS].reverse().map(tier => (
            <div key={tier.id} className="flex items-center gap-3">
              <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold border shrink-0 ${tier.bgColor} ${tier.color}`}>
                {tier.emoji} {tier.name}
              </span>
              <p className="text-xs text-muted-foreground">{tier.minReferrals}+ members brought in</p>
            </div>
          ))}
        </div>
        <p className="text-xs text-muted-foreground border-t border-border/60 pt-3">
          A member is confirmed when they join via your link and make their first BCH post. Badges appear on your profile automatically.
        </p>
      </div>

    </div>
  );
}

// ── Podium card (top 3) ──────────────────────────────────────────
const PODIUM_HEIGHTS = ['h-24', 'h-16', 'h-12'];
const PODIUM_COLORS = [
  'bg-amber-500/20 border-amber-500/40',
  'bg-slate-400/15 border-slate-400/30',
  'bg-orange-400/10 border-orange-400/20',
];
const CROWN_COLORS = ['text-amber-500', 'text-slate-400', 'text-orange-400'];
const MEDALS = ['🥇', '🥈', '🥉'];

function PodiumCard({ entry, rank, isMe }: { entry: { pubkey: string; count: number }; rank: number; isMe: boolean }) {
  const { profile } = useNostrProfile(entry.pubkey);
  const badge = getRecruiterBadge(entry.count);
  const idx = rank - 1;

  return (
    <Link to={`/profile/${entry.pubkey}`} className="flex-1 min-w-0 flex flex-col items-center gap-1.5 group">
      {/* Avatar */}
      <div className="relative">
        {rank === 1 && <Crown className="w-5 h-5 text-amber-500 absolute -top-3 left-1/2 -translate-x-1/2" />}
        {profile?.picture ? (
          <img src={profile.picture} alt="" className={`${rank === 1 ? 'w-14 h-14' : 'w-11 h-11'} rounded-full object-cover ring-2 ${rank === 1 ? 'ring-amber-500/50' : 'ring-border'} group-hover:ring-primary/50 transition-all`} />
        ) : (
          <div className={`${rank === 1 ? 'w-14 h-14' : 'w-11 h-11'} rounded-full bg-primary/10 flex items-center justify-center ring-2 ring-border`}>
            <User className="w-5 h-5 text-primary/60" />
          </div>
        )}
        {isMe && <span className="absolute -bottom-1 -right-1 text-[8px] bg-primary text-primary-foreground rounded-full px-1 font-bold">You</span>}
      </div>

      {/* Name + badge */}
      <div className="text-center max-w-full px-1">
        <p className={`${rank === 1 ? 'text-sm' : 'text-xs'} font-bold truncate leading-tight`}>
          {profile?.display_name || profile?.name || entry.pubkey.slice(0, 8) + '…'}
        </p>
        {badge && (
          <span className={`inline-flex items-center gap-0.5 px-1.5 py-0 rounded-full text-[9px] font-bold border ${badge.bgColor} ${badge.color}`}>
            {badge.emoji} {badge.name}
          </span>
        )}
      </div>

      {/* Podium block */}
      <div className={`w-full ${PODIUM_HEIGHTS[idx]} rounded-t-xl border-t-2 ${PODIUM_COLORS[idx]} flex flex-col items-center justify-center gap-0.5`}>
        <span className="text-lg">{MEDALS[idx]}</span>
        <div className={`flex items-center gap-0.5 font-extrabold ${rank === 1 ? 'text-base' : 'text-sm'} ${CROWN_COLORS[idx]}`}>
          <Users className="w-3 h-3" />
          <span>{entry.count}</span>
        </div>
      </div>
    </Link>
  );
}

// ── Regular list row (rank 4+) ──────────────────────────────────
function AmbassadorRow({ pubkey, count, rank, isMe }: { pubkey: string; count: number; rank: number; isMe: boolean }) {
  const { profile } = useNostrProfile(pubkey);
  const badge = getRecruiterBadge(count);

  return (
    <div className={`flex items-center gap-3 px-4 py-3 hover:bg-muted/30 transition-colors ${isMe ? 'bg-primary/5 border-l-2 border-primary' : ''}`}>
      <span className="w-7 text-center text-sm font-bold text-muted-foreground shrink-0">{rank}</span>
      <Link to={`/profile/${pubkey}`} className="shrink-0">
        {profile?.picture ? (
          <img src={profile.picture} alt="" className="w-10 h-10 rounded-full object-cover" />
        ) : (
          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
            <User className="w-5 h-5 text-primary/60" />
          </div>
        )}
      </Link>
      <Link to={`/profile/${pubkey}`} className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 flex-wrap">
          <p className="font-semibold text-sm truncate">
            {profile?.display_name || profile?.name || pubkey.slice(0, 12) + '…'}
          </p>
          {badge && (
            <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[9px] font-bold border ${badge.bgColor} ${badge.color}`}>
              {badge.emoji} {badge.name}
            </span>
          )}
          {isMe && <span className="text-[9px] text-primary font-bold">You</span>}
        </div>
        <UserBadges pubkey={pubkey} size="sm" />
        {profile?.nip05 && <p className="text-xs text-muted-foreground truncate">{profile.nip05}</p>}
      </Link>
      <FollowButton pubkey={pubkey} size="sm" />
      <div className="text-right shrink-0">
        <div className="flex items-center gap-1 text-amber-500 font-extrabold text-sm">
          <Users className="w-3.5 h-3.5" />
          <span>{count}</span>
        </div>
        <p className="text-[10px] text-muted-foreground">member{count !== 1 ? 's' : ''}</p>
      </div>
    </div>
  );
}

